<template>
  <div class="crm-agenda-page">
    <div class="page-header">
      <div class="header-top">
        <div>
          <h2 class="page-title mb-25">CRM - Agenda</h2>
          <p class="page-subtitle text-muted mb-0">Gerencie suas atividades e compromissos</p>
        </div>
        <div class="header-actions">
          <div class="view-toggle">
            <b-button
              :variant="viewMode === 'semana' ? 'primary' : 'outline-secondary'"
              size="sm"
              @click="switchView('semana')"
              title="Visão Semanal"
            >
              <feather-icon icon="CalendarIcon" size="14" />
            </b-button>
            <b-button
              :variant="viewMode === 'lista' ? 'primary' : 'outline-secondary'"
              size="sm"
              @click="switchView('lista')"
              title="Visão Lista"
            >
              <feather-icon icon="ListIcon" size="14" />
            </b-button>
            <b-button
              :variant="viewMode === 'cards' ? 'primary' : 'outline-secondary'"
              size="sm"
              @click="switchView('cards')"
              title="Visão Cards"
            >
              <feather-icon icon="GridIcon" size="14" />
            </b-button>
          </div>
          <b-button variant="primary" size="sm" @click="openAtividadeModal()">
            <feather-icon icon="PlusIcon" size="14" class="mr-25" /> Nova Atividade
          </b-button>
        </div>
      </div>
    </div>

    <div v-if="viewMode === 'semana'" class="week-view-container">
      <div class="week-nav">
        <b-button variant="flat-secondary" size="sm" @click="navigateWeek(-1)">
          <feather-icon icon="ChevronLeftIcon" size="16" />
        </b-button>
        <div class="week-nav-label">
          <span class="week-range-text">{{ weekRangeLabel }}</span>
          <b-button
            v-if="!isCurrentWeek"
            variant="outline-primary"
            size="sm"
            class="ml-75 btn-today"
            @click="goToCurrentWeek"
          >Hoje</b-button>
        </div>
        <b-button variant="flat-secondary" size="sm" @click="navigateWeek(1)">
          <feather-icon icon="ChevronRightIcon" size="16" />
        </b-button>
      </div>

      <div class="week-filters">
        <b-form-select v-model="filters.tipo" size="sm" class="week-filter-select" @change="loadWeekActivities">
          <b-form-select-option :value="null">Todos os tipos</b-form-select-option>
          <b-form-select-option v-for="t in tipoOptions" :key="t.value" :value="t.value">{{ t.text }}</b-form-select-option>
        </b-form-select>
        <b-form-select v-model="filters.status" size="sm" class="week-filter-select" @change="loadWeekActivities">
          <b-form-select-option :value="null">Todos os status</b-form-select-option>
          <b-form-select-option value="PENDENTE">Pendente</b-form-select-option>
          <b-form-select-option value="CONCLUIDA">Concluída</b-form-select-option>
          <b-form-select-option value="CANCELADA">Cancelada</b-form-select-option>
        </b-form-select>
      </div>

      <div v-if="loading" class="text-center py-4">
        <b-spinner variant="primary" />
        <p class="mt-1 text-muted">Carregando atividades...</p>
      </div>
      <div v-else class="week-board">
        <div
          v-for="day in weekDays"
          :key="day.dateStr"
          class="week-column"
          :class="{ 'is-today': day.isToday }"
        >
          <div class="week-column-header">
            <div class="day-info">
              <span class="day-name">{{ day.dayName }}</span>
              <span class="day-number">{{ day.dayNumber }}</span>
              <b-badge
                v-if="getActivitiesForDay(day.dateStr).length > 0"
                variant="primary"
                pill
                class="day-count"
              >{{ getActivitiesForDay(day.dateStr).length }}</b-badge>
            </div>
            <b-button
              variant="flat-primary"
              size="sm"
              class="btn-icon btn-add-day"
              @click="openAtividadeModal(null, day.dateStr)"
              title="Adicionar atividade"
            >
              <feather-icon icon="PlusIcon" size="14" />
            </b-button>
          </div>
          <div class="week-column-body">
            <div
              v-for="atv in getActivitiesForDay(day.dateStr)"
              :key="atv.id"
              class="week-activity-card"
              :class="'type-' + (atv.tipo || '').toLowerCase()"
            >
              <div class="wac-header">
                <span class="wac-title" :title="atv.titulo">{{ atv.titulo }}</span>
                <div class="wac-actions">
                  <b-button
                    variant="flat-secondary"
                    size="sm"
                    class="btn-icon wac-btn"
                    @click="openAtividadeModal(atv)"
                    title="Editar"
                  >
                    <feather-icon icon="EditIcon" size="12" />
                  </b-button>
                  <b-button
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon wac-btn"
                    @click="deleteAtividade(atv)"
                    title="Excluir"
                  >
                    <feather-icon icon="TrashIcon" size="12" />
                  </b-button>
                </div>
              </div>
              <div class="wac-assignee text-body">{{ atv.criado_por_nome || 'Sem responsável' }}</div>
              <div class="wac-meta text-muted" v-if="atv.cliente_nome || atv.cliente_razao_social">
                <feather-icon icon="BriefcaseIcon" size="11" class="wac-meta-icon" />
                <span>{{ atv.cliente_nome || atv.cliente_razao_social }}</span>
              </div>
              <div class="wac-meta text-muted" v-if="atv.local">
                <feather-icon icon="MapPinIcon" size="11" class="wac-meta-icon" />
                <span>{{ atv.local }}</span>
              </div>
              <div class="wac-status-row" v-if="atv.status === 'PENDENTE'">
                <b-button
                  variant="flat-success"
                  size="sm"
                  class="wac-complete-btn"
                  @click="concluirAtividade(atv)"
                  title="Concluir"
                >
                  <feather-icon icon="CheckIcon" size="12" class="mr-25" />Concluir
                </b-button>
              </div>
              <div class="wac-status-row" v-else-if="atv.status === 'CONCLUIDA'">
                <b-badge variant="light-success">
                  <feather-icon icon="CheckCircleIcon" size="11" class="mr-25" />Concluída
                </b-badge>
              </div>
              <div class="wac-status-row" v-else-if="atv.status === 'CANCELADA'">
                <b-badge variant="light-danger">
                  <feather-icon icon="XCircleIcon" size="11" class="mr-25" />Cancelada
                </b-badge>
              </div>
            </div>
            <div v-if="getActivitiesForDay(day.dateStr).length === 0" class="week-empty-day text-muted">
              <span>Sem atividades</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="viewMode !== 'semana'">
      <b-card class="filters-card" no-body>
        <div class="filters-bar">
          <b-row align-v="end">
            <b-col md="3" lg="2">
              <b-form-group label="Período" label-size="sm" class="mb-0">
                <b-form-select v-model="filters.periodo" size="sm" @change="onPeriodoChange">
                  <b-form-select-option value="hoje">Hoje</b-form-select-option>
                  <b-form-select-option value="7d">Últimos 7 dias</b-form-select-option>
                  <b-form-select-option value="30d">Últimos 30 dias</b-form-select-option>
                  <b-form-select-option value="mes">Este mês</b-form-select-option>
                  <b-form-select-option value="personalizado">Personalizado</b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col v-if="filters.periodo === 'personalizado'" md="2">
              <b-form-group label="De" label-size="sm" class="mb-0">
                <b-form-input v-model="filters.data_inicio" type="date" size="sm" @change="loadAtividades" />
              </b-form-group>
            </b-col>
            <b-col v-if="filters.periodo === 'personalizado'" md="2">
              <b-form-group label="Até" label-size="sm" class="mb-0">
                <b-form-input v-model="filters.data_fim" type="date" size="sm" @change="loadAtividades" />
              </b-form-group>
            </b-col>
            <b-col md="3" lg="2">
              <b-form-group label="Tipo" label-size="sm" class="mb-0">
                <b-form-select v-model="filters.tipo" size="sm" @change="onFilterChange">
                  <b-form-select-option :value="null">Todos</b-form-select-option>
                  <b-form-select-option v-for="t in tipoOptions" :key="t.value" :value="t.value">{{ t.text }}</b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="3" lg="2">
              <b-form-group label="Status" label-size="sm" class="mb-0">
                <b-form-select v-model="filters.status" size="sm" @change="onFilterChange">
                  <b-form-select-option :value="null">Todos</b-form-select-option>
                  <b-form-select-option value="PENDENTE">Pendente</b-form-select-option>
                  <b-form-select-option value="CONCLUIDA">Concluída</b-form-select-option>
                  <b-form-select-option value="CANCELADA">Cancelada</b-form-select-option>
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
      </b-card>

      <div v-if="viewMode === 'lista'">
        <b-card no-body class="table-card">
          <b-table
            :items="atividades"
            :fields="fields"
            :busy="loading"
            responsive
            hover
            show-empty
            empty-text="Nenhuma atividade encontrada"
            class="mb-0 crm-table"
          >
            <template #table-busy>
              <div class="text-center py-4">
                <b-spinner variant="primary" />
                <p class="mt-1 text-muted">Carregando atividades...</p>
              </div>
            </template>

            <template #cell(tipo)="{ item }">
              <b-badge :variant="getTipoBadgeVariant(item.tipo)" pill>{{ formatTipo(item.tipo) }}</b-badge>
            </template>

            <template #cell(cliente_nome)="{ item }">
              <span
                v-if="item.cliente_id"
                class="client-link text-primary"
                @click="$router.push({ name: 'crm-cliente-detail', params: { id: item.cliente_id } })"
              >
                {{ item.cliente_nome || item.cliente_razao_social || '-' }}
              </span>
              <span v-else>-</span>
            </template>

            <template #cell(data_inicio)="{ item }">
              {{ formatDateTime(item.data_inicio) }}
            </template>

            <template #cell(status)="{ item }">
              <b-badge :variant="getStatusBadgeVariant(item.status)" pill>{{ item.status }}</b-badge>
            </template>

            <template #cell(actions)="{ item }">
              <b-button
                v-if="item.status === 'PENDENTE'"
                variant="flat-success"
                size="sm"
                title="Concluir"
                @click="concluirAtividade(item)"
                class="mr-25"
              >
                <feather-icon icon="CheckIcon" size="14" />
              </b-button>
              <b-button
                v-if="item.status === 'PENDENTE'"
                variant="flat-danger"
                size="sm"
                title="Cancelar"
                @click="cancelarAtividade(item)"
                class="mr-25"
              >
                <feather-icon icon="XIcon" size="14" />
              </b-button>
              <b-button variant="flat-primary" size="sm" title="Editar" @click="openAtividadeModal(item)">
                <feather-icon icon="EditIcon" size="14" />
              </b-button>
            </template>
          </b-table>

          <div class="table-footer" v-if="totalRows > 0">
            <span class="table-info">
              Mostrando {{ ((currentPage - 1) * perPage) + 1 }}
              a {{ Math.min(currentPage * perPage, totalRows) }}
              de {{ totalRows }} atividades
            </span>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              size="sm"
              class="mb-0"
              @change="onPageChange"
            />
          </div>
        </b-card>
      </div>

      <div v-if="viewMode === 'cards'">
        <div v-if="loading" class="text-center py-4">
          <b-spinner variant="primary" />
        </div>
        <div v-else-if="atividades.length === 0" class="text-center py-4 text-muted">
          Nenhuma atividade encontrada
        </div>
        <div v-else class="cards-grid">
          <b-card v-for="atv in atividades" :key="atv.id" class="activity-card" no-body>
            <div class="activity-card-body">
              <div class="activity-card-header">
                <b-badge :variant="getTipoBadgeVariant(atv.tipo)" pill>{{ formatTipo(atv.tipo) }}</b-badge>
                <b-badge :variant="getStatusBadgeVariant(atv.status)" pill>{{ atv.status }}</b-badge>
              </div>
              <h6 class="activity-card-title">{{ atv.titulo }}</h6>
              <p class="activity-card-desc" v-if="atv.descricao">{{ atv.descricao }}</p>
              <div class="activity-card-meta">
                <span v-if="atv.cliente_nome || atv.cliente_razao_social">
                  <feather-icon icon="UserIcon" size="12" class="mr-25" />
                  {{ atv.cliente_nome || atv.cliente_razao_social }}
                </span>
                <span>
                  <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
                  {{ formatDateTime(atv.data_inicio) }}
                </span>
                <span v-if="atv.local">
                  <feather-icon icon="MapPinIcon" size="12" class="mr-25" />
                  {{ atv.local }}
                </span>
              </div>
              <div class="activity-card-actions" v-if="atv.status === 'PENDENTE'">
                <b-button variant="success" size="sm" @click="concluirAtividade(atv)">
                  <feather-icon icon="CheckIcon" size="14" class="mr-25" /> Concluir
                </b-button>
                <b-button variant="outline-danger" size="sm" @click="cancelarAtividade(atv)">
                  <feather-icon icon="XIcon" size="14" class="mr-25" /> Cancelar
                </b-button>
              </div>
            </div>
          </b-card>
        </div>
        <div class="text-center mt-1" v-if="totalRows > perPage">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
            align="center"
            @change="onPageChange"
          />
        </div>
      </div>
    </template>

    <b-modal
      v-model="atividadeModal.show"
      :title="atividadeModal.editing ? 'Editar Atividade' : 'Nova Atividade'"
      centered
      size="lg"
      @ok.prevent="saveAtividade"
      ok-title="Salvar"
      cancel-title="Cancelar"
    >
      <b-row>
        <b-col md="8">
          <b-form-group label="Título">
            <b-form-input v-model="atividadeModal.form.titulo" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Tipo">
            <b-form-select v-model="atividadeModal.form.tipo">
              <b-form-select-option v-for="t in tipoOptions" :key="t.value" :value="t.value">{{ t.text }}</b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Descrição">
        <b-form-textarea v-model="atividadeModal.form.descricao" rows="3" />
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group label="Data/Hora Início">
            <b-form-input v-model="atividadeModal.form.data_inicio" type="datetime-local" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Data/Hora Fim">
            <b-form-input v-model="atividadeModal.form.data_fim" type="datetime-local" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-form-group label="Local">
            <b-form-input v-model="atividadeModal.form.local" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Cliente">
            <div class="cliente-search-wrapper">
              <b-form-input
                v-model="atividadeModal.clienteSearch"
                placeholder="Buscar cliente..."
                @input="searchClientes"
              />
              <div
                v-if="atividadeModal.clienteSearch && (atividadeModal.clienteLoading || atividadeModal.clienteResults.length || atividadeModal.clienteNoResults)"
                class="search-results"
              >
                <div v-if="atividadeModal.clienteLoading" class="search-loading text-muted">
                  <b-spinner small variant="primary" class="mr-50" /> Buscando clientes...
                </div>
                <div v-else-if="atividadeModal.clienteNoResults" class="search-empty text-muted">
                  Nenhum cliente encontrado
                </div>
                <div
                  v-else
                  v-for="c in atividadeModal.clienteResults"
                  :key="c.clienteid"
                  class="search-result-item"
                  @click="selectCliente(c)"
                >
                  {{ c.clienterazaosocial }} <small class="text-muted">{{ c.clientecnpj }}</small>
                </div>
              </div>
            </div>
            <small v-if="atividadeModal.form.cliente_id" class="text-muted">
              Cliente selecionado: {{ atividadeModal.clienteNome }}
            </small>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Oportunidade ID (opcional)">
        <b-form-input v-model="atividadeModal.form.oportunidade_id" placeholder="ID da oportunidade" />
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="deleteConfirm.show"
      title="Excluir Atividade"
      ok-title="Excluir"
      ok-variant="danger"
      cancel-title="Cancelar"
      centered
      @ok="confirmDeleteAtividade"
    >
      <p>Tem certeza que deseja excluir a atividade <strong>{{ deleteConfirm.item ? deleteConfirm.item.titulo : '' }}</strong>?</p>
      <p class="text-muted" style="font-size: 0.85rem;">Esta ação não pode ser desfeita.</p>
    </b-modal>
  </div>
</template>

<script>
import crmClient from '@/api/CRM/crm.client';
import {
  BCard, BTable, BPagination, BFormInput, BFormSelect,
  BFormSelectOption, BButton, BBadge, BSpinner, BRow, BCol,
  BFormGroup, BModal, BFormTextarea, BOverlay,
} from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

const DAY_NAMES = ['DOMINGO', 'SEGUNDA', 'TERÇA', 'QUARTA', 'QUINTA', 'SEXTA', 'SÁBADO'];

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function formatDateISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

export default {
  name: 'CrmAgendaPage',
  components: {
    BCard, BTable, BPagination, BFormInput, BFormSelect,
    BFormSelectOption, BButton, BBadge, BSpinner, BRow, BCol,
    BFormGroup, BModal, BFormTextarea, BOverlay,
  },
  data() {
    return {
      loading: false,
      viewMode: 'semana',
      currentWeekStart: getMonday(new Date()),
      weekActivities: [],
      atividades: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 20,
      filters: {
        periodo: '30d',
        data_inicio: '',
        data_fim: '',
        tipo: null,
        status: null,
      },
      fields: [
        { key: 'titulo', label: 'Título', sortable: true },
        { key: 'tipo', label: 'Tipo' },
        { key: 'cliente_nome', label: 'Cliente' },
        { key: 'data_inicio', label: 'Data', sortable: true },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Ações', class: 'text-center' },
      ],
      tipoOptions: [
        { value: 'LIGACAO', text: 'Ligação' },
        { value: 'CALL', text: 'Call' },
        { value: 'VISITA_PRESENCIAL', text: 'Visita Presencial' },
        { value: 'REUNIAO', text: 'Reunião' },
        { value: 'FOLLOWUP', text: 'Follow-up' },
        { value: 'EMAIL', text: 'E-mail' },
        { value: 'PROSPECCAO', text: 'Prospecção' },
        { value: 'INTERNA', text: 'Interna' },
        { value: 'PRE_PEDIDO_PIC', text: 'Pré-pedido/PIC' },
      ],
      atividadeModal: {
        show: false,
        editing: false,
        editId: null,
        form: {
          titulo: '',
          descricao: '',
          tipo: 'LIGACAO',
          data_inicio: '',
          data_fim: '',
          local: '',
          cliente_id: '',
          oportunidade_id: '',
        },
        clienteSearch: '',
        clienteNome: '',
        clienteResults: [],
        clienteLoading: false,
        clienteNoResults: false,
      },
      deleteConfirm: {
        show: false,
        item: null,
      },
      searchTimeout: null,
    };
  },
  computed: {
    weekDays() {
      const days = [];
      const todayStr = formatDateISO(new Date());
      for (let i = 0; i < 5; i++) {
        const d = new Date(this.currentWeekStart);
        d.setDate(d.getDate() + i);
        const dateStr = formatDateISO(d);
        days.push({
          dateStr,
          dayName: DAY_NAMES[d.getDay()],
          dayNumber: d.getDate(),
          isToday: dateStr === todayStr,
        });
      }
      return days;
    },
    weekRangeLabel() {
      const start = this.currentWeekStart;
      const end = new Date(start);
      end.setDate(end.getDate() + 4);
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      const startLabel = `${start.getDate()} ${months[start.getMonth()]}`;
      const endLabel = `${end.getDate()} ${months[end.getMonth()]} ${end.getFullYear()}`;
      return `${startLabel} — ${endLabel}`;
    },
    isCurrentWeek() {
      const current = getMonday(new Date());
      return formatDateISO(current) === formatDateISO(this.currentWeekStart);
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    switchView(mode) {
      this.viewMode = mode;
      this.loadData();
    },
    loadData() {
      if (this.viewMode === 'semana') {
        this.loadWeekActivities();
      } else {
        this.loadAtividades();
      }
    },
    async loadWeekActivities() {
      this.loading = true;
      try {
        const start = formatDateISO(this.currentWeekStart);
        const end = new Date(this.currentWeekStart);
        end.setDate(end.getDate() + 4);
        const endStr = formatDateISO(end);

        const params = {
          data_inicio: start,
          data_fim: endStr,
          limit: 500,
          page: 1,
        };
        if (this.filters.tipo) params.tipo = this.filters.tipo;
        if (this.filters.status) params.status = this.filters.status;

        const result = await crmClient.listAtividades(params);
        const payload = result.data || result;
        this.weekActivities = payload.items || payload.rows || (Array.isArray(payload) ? payload : []);
      } catch (error) {
        console.error('Erro ao carregar atividades da semana:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar atividades da semana',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    getActivitiesForDay(dateStr) {
      return this.weekActivities.filter(a => {
        if (!a.data_inicio) return false;
        const d = new Date(a.data_inicio);
        const aDate = formatDateISO(d);
        return aDate === dateStr;
      });
    },
    navigateWeek(direction) {
      const newStart = new Date(this.currentWeekStart);
      newStart.setDate(newStart.getDate() + (direction * 7));
      this.currentWeekStart = newStart;
      this.loadWeekActivities();
    },
    goToCurrentWeek() {
      this.currentWeekStart = getMonday(new Date());
      this.loadWeekActivities();
    },
    getDateRange() {
      const now = new Date();
      let inicio = '';
      let fim = '';
      switch (this.filters.periodo) {
        case 'hoje': {
          const d = now.toISOString().slice(0, 10);
          inicio = d; fim = d;
          break;
        }
        case '7d': {
          const past = new Date(now); past.setDate(past.getDate() - 7);
          inicio = past.toISOString().slice(0, 10);
          fim = now.toISOString().slice(0, 10);
          break;
        }
        case '30d': {
          const past = new Date(now); past.setDate(past.getDate() - 30);
          inicio = past.toISOString().slice(0, 10);
          fim = now.toISOString().slice(0, 10);
          break;
        }
        case 'mes': {
          inicio = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
          fim = now.toISOString().slice(0, 10);
          break;
        }
        case 'personalizado':
          inicio = this.filters.data_inicio;
          fim = this.filters.data_fim;
          break;
      }
      return { inicio, fim };
    },
    async loadAtividades() {
      this.loading = true;
      try {
        const { inicio, fim } = this.getDateRange();
        const params = { page: this.currentPage, limit: this.perPage };
        if (inicio) params.data_inicio = inicio;
        if (fim) params.data_fim = fim;
        if (this.filters.tipo) params.tipo = this.filters.tipo;
        if (this.filters.status) params.status = this.filters.status;

        const result = await crmClient.listAtividades(params);
        const payload = result.data || result;
        this.atividades = payload.items || payload.rows || (Array.isArray(payload) ? payload : []);
        this.totalRows = payload.total || payload.count || this.atividades.length;
      } catch (error) {
        console.error('Erro ao carregar atividades:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar atividades',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onPeriodoChange() {
      if (this.filters.periodo !== 'personalizado') {
        this.currentPage = 1;
        this.loadAtividades();
      }
    },
    onFilterChange() {
      this.currentPage = 1;
      this.loadAtividades();
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadAtividades();
    },
    openAtividadeModal(atv, prefillDate) {
      if (atv) {
        this.atividadeModal.editing = true;
        this.atividadeModal.editId = atv.id;
        this.atividadeModal.form = {
          titulo: atv.titulo || '',
          descricao: atv.descricao || '',
          tipo: atv.tipo || 'LIGACAO',
          data_inicio: atv.data_inicio ? atv.data_inicio.slice(0, 16) : '',
          data_fim: atv.data_fim ? atv.data_fim.slice(0, 16) : '',
          local: atv.local || '',
          cliente_id: atv.cliente_id || '',
          oportunidade_id: atv.oportunidade_id || '',
        };
        this.atividadeModal.clienteNome = atv.cliente_nome || atv.cliente_razao_social || '';
        this.atividadeModal.clienteSearch = '';
      } else {
        this.atividadeModal.editing = false;
        this.atividadeModal.editId = null;
        const defaultDate = prefillDate ? `${prefillDate}T09:00` : '';
        this.atividadeModal.form = {
          titulo: '', descricao: '', tipo: 'LIGACAO',
          data_inicio: defaultDate, data_fim: '', local: '',
          cliente_id: '', oportunidade_id: '',
        };
        this.atividadeModal.clienteSearch = '';
        this.atividadeModal.clienteNome = '';
      }
      this.atividadeModal.clienteResults = [];
      this.atividadeModal.show = true;
    },
    async saveAtividade() {
      try {
        const payload = { ...this.atividadeModal.form };
        if (!payload.oportunidade_id) delete payload.oportunidade_id;
        if (!payload.cliente_id) delete payload.cliente_id;

        if (this.atividadeModal.editing) {
          await crmClient.updateAtividade(this.atividadeModal.editId, payload);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Atividade atualizada',
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
        } else {
          await crmClient.createAtividade(payload);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Atividade criada',
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
        }
        this.atividadeModal.show = false;
        this.loadData();
      } catch (error) {
        console.error('Erro ao salvar atividade:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao salvar atividade',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async concluirAtividade(atv) {
      if (!confirm('Deseja concluir esta atividade?')) return;
      try {
        await crmClient.concluirAtividade(atv.id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atividade concluída',
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
        this.loadData();
      } catch (error) {
        console.error('Erro ao concluir atividade:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao concluir atividade',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async cancelarAtividade(atv) {
      const motivo = prompt('Motivo do cancelamento:');
      if (motivo === null) return;
      try {
        await crmClient.cancelarAtividade(atv.id, motivo);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atividade cancelada',
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
        this.loadData();
      } catch (error) {
        console.error('Erro ao cancelar atividade:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao cancelar atividade',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async deleteAtividade(atv) {
      this.deleteConfirm.item = atv;
      this.deleteConfirm.show = true;
    },
    async confirmDeleteAtividade() {
      const atv = this.deleteConfirm.item;
      if (!atv) return;
      try {
        await crmClient.deleteAtividade(atv.id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atividade excluída',
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
        this.loadData();
      } catch (error) {
        console.error('Erro ao excluir atividade:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao excluir atividade',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.deleteConfirm.show = false;
        this.deleteConfirm.item = null;
      }
    },
    searchClientes(query) {
      clearTimeout(this.searchTimeout);
      if (!query || query.length < 2) {
        this.atividadeModal.clienteResults = [];
        this.atividadeModal.clienteLoading = false;
        this.atividadeModal.clienteNoResults = false;
        return;
      }
      this.searchTimeout = setTimeout(async () => {
        this.atividadeModal.clienteLoading = true;
        this.atividadeModal.clienteNoResults = false;
        try {
          const result = await crmClient.listClientes({ query: query, limit: 10 });
          const payload = result.data || result;
          this.atividadeModal.clienteResults = payload.items || payload.rows || (Array.isArray(payload) ? payload : []);
          this.atividadeModal.clienteNoResults = this.atividadeModal.clienteResults.length === 0;
        } catch (e) {
          this.atividadeModal.clienteResults = [];
          this.atividadeModal.clienteNoResults = true;
        } finally {
          this.atividadeModal.clienteLoading = false;
        }
      }, 300);
    },
    selectCliente(c) {
      this.atividadeModal.form.cliente_id = c.clienteid;
      this.atividadeModal.clienteNome = c.clienterazaosocial;
      this.atividadeModal.clienteSearch = '';
      this.atividadeModal.clienteResults = [];
    },
    getTipoBadgeVariant(tipo) {
      const map = {
        LIGACAO: 'info', CALL: 'primary', VISITA_PRESENCIAL: 'success',
        REUNIAO: 'warning', FOLLOWUP: 'secondary', EMAIL: 'dark',
        PROSPECCAO: 'info', INTERNA: 'light', PRE_PEDIDO_PIC: 'primary',
      };
      return map[tipo] || 'light';
    },
    getStatusBadgeVariant(status) {
      const map = { PENDENTE: 'warning', CONCLUIDA: 'success', CANCELADA: 'danger' };
      return map[status] || 'secondary';
    },
    formatTipo(tipo) {
      const found = this.tipoOptions.find(t => t.value === tipo);
      return found ? found.text : tipo || 'N/A';
    },
    formatDateTime(val) {
      if (!val) return '-';
      return new Date(val).toLocaleDateString('pt-BR') + ' ' + new Date(val).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
.crm-agenda-page {
  padding: 0.75rem;
}
.page-header {
  margin-bottom: 1.25rem;
}
.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
}
.page-title {
  font-size: 1.5rem;
  font-weight: 600;
}
.page-subtitle {
  font-size: 0.95rem;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.view-toggle {
  display: flex;
  gap: 0.25rem;
}

.week-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
}
.week-nav-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.week-range-text {
  font-size: 1rem;
  font-weight: 600;
  min-width: 200px;
  text-align: center;
}
.btn-today {
  font-size: 0.75rem;
  padding: 0.15rem 0.6rem;
}

.week-filters {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}
.week-filter-select {
  max-width: 180px;
  font-size: 0.82rem;
}

.week-board {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 1rem;
  min-height: 400px;
}
.week-column {
  flex: 1;
  min-width: 180px;
  border-radius: 10px;
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.08));
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.week-column.is-today {
  border-color: var(--primary);
  box-shadow: 0 0 0 1px var(--primary);
}
.week-column-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.08));
}
.day-info {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.day-name {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.day-number {
  font-size: 1.15rem;
  font-weight: 700;
}
.week-column.is-today .day-name,
.week-column.is-today .day-number {
  color: var(--primary);
}
.day-count {
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
}
.btn-add-day.btn-add-day {
  padding: 0.25rem;
}
.week-column-body {
  flex: 1;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  overflow-y: auto;
  max-height: calc(100vh - 280px);
}
.week-empty-day {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 80px;
  font-size: 0.78rem;
  font-style: italic;
}

.week-activity-card {
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.08));
  border-left: 3px solid var(--primary);
  border-radius: 6px;
  padding: 0.55rem 0.6rem;
  cursor: default;
  transition: box-shadow 0.15s ease, transform 0.1s ease;
}
.week-activity-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}
.week-activity-card.type-ligacao,
.week-activity-card.type-prospeccao {
  border-left-color: var(--info);
}
.week-activity-card.type-call,
.week-activity-card.type-pre_pedido_pic {
  border-left-color: var(--primary);
}
.week-activity-card.type-visita_presencial {
  border-left-color: var(--success);
}
.week-activity-card.type-reuniao {
  border-left-color: var(--warning);
}
.week-activity-card.type-followup,
.week-activity-card.type-email,
.week-activity-card.type-interna {
  border-left-color: var(--secondary);
}

.wac-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.3rem;
  margin-bottom: 0.2rem;
}
.wac-title {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  line-height: 1.3;
}
.wac-actions {
  display: flex;
  gap: 0.15rem;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.week-activity-card:hover .wac-actions {
  opacity: 1;
}
.wac-btn.wac-btn {
  padding: 0.2rem;
}
.wac-assignee {
  font-size: 0.72rem;
  margin-bottom: 0.25rem;
}
.wac-meta {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  margin-top: 0.15rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.wac-meta-icon {
  flex-shrink: 0;
}
.wac-status-row {
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
}
.wac-complete-btn {
  font-size: 0.68rem;
  padding: 0.2rem 0.5rem;
}

.filters-card {
  margin-bottom: 1rem;
}
.filters-bar {
  padding: 1rem 1.25rem;
}
.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.25rem;
}
.table-info {
  font-size: 0.85rem;
}
.client-link {
  cursor: pointer;
  font-weight: 500;
}
.client-link:hover {
  text-decoration: underline;
}
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}
.activity-card-body {
  padding: 1rem 1.25rem;
}
.activity-card-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.activity-card-title {
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.activity-card-desc {
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
}
.activity-card-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.82rem;
  margin-bottom: 0.75rem;
}
.activity-card-actions {
  display: flex;
  gap: 0.5rem;
}
.cliente-search-wrapper {
  position: relative;
}
.search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  border-radius: 6px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.search-result-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;
  border-bottom: 1px solid var(--border-color, rgba(0, 0, 0, 0.08));
}
.search-result-item:last-child {
  border-bottom: none;
}
.search-result-item:hover {
  background: var(--bg-hover, rgba(0, 0, 0, 0.04));
}
.search-loading,
.search-empty {
  padding: 0.6rem 0.75rem;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .crm-agenda-page {
    padding: 1rem;
  }
  .header-top {
    flex-direction: column;
  }
  .week-board {
    gap: 0.4rem;
  }
  .week-column {
    min-width: 160px;
  }
  .day-name {
    font-size: 0.6rem;
  }
  .day-number {
    font-size: 1rem;
  }
}
</style>
